(() => {
  'use strict';

  const CONFIG = Object.freeze({
    minViewedProducts: 3,
    mobileMinViewedProducts: 4,
    minDisplayProducts: 4,
    maxDisplayProducts: 6,
    minPageAgeMs: 8000,
    dismissDays: 5,
    desktopExitTopPx: 12,
    mobileDeepScrollPx: 600,
    mobileTopPx: 120,
    mobileReturnWindowMs: 1400,
    exitShownSessionKey: 'sct_rec_exit_auto_v1',
    surfaceSessionKey: 'sct_rec_surface_seen_v1',
    dismissedAtKey: 'sct_rec_exit_dismissed_at_v1',
    sessionAtcKey: 'sct_rec_session_atc_v1',
  });

  const query =
    new URLSearchParams(
      window.location.search
    );

  const debug =
    query.get('sct_debug') === '1';

  const pageLoadedAt =
    Date.now();

  const coarsePointer =
    window.matchMedia?.(
      '(pointer: coarse)'
    )?.matches ||
    navigator.maxTouchPoints > 0;

  let loadingPromise = null;
  let products = [];
  let opening = false;
  let lastDeepSeenAt = 0;

  function toNumber(value) {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : 0;
  }

  function profile() {
    return (
      window.SctRecommendation
        ?.getProfile?.() ||
      null
    );
  }

  function viewedCount() {
    return toNumber(
      profile()
        ?.uniqueViewedProducts
    );
  }

  function readContext() {
    const node =
      document.getElementById(
        'SctRecommendationContext'
      );

    if (!node) return {};

    try {
      return JSON.parse(
        node.textContent || '{}'
      );
    } catch (_error) {
      return {};
    }
  }

  function sessionValue(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function writeSession(
    key,
    value
  ) {
    try {
      sessionStorage.setItem(
        key,
        value
      );
    } catch (_error) {
      // no-op
    }
  }

  function localNumber(key) {
    try {
      return Number(
        localStorage.getItem(key)
      ) || 0;
    } catch (_error) {
      return 0;
    }
  }

  function writeLocalNumber(
    key,
    value
  ) {
    try {
      localStorage.setItem(
        key,
        String(value)
      );
    } catch (_error) {
      // no-op
    }
  }

  function surfaceSeen() {
    return (
      sessionValue(
        CONFIG.surfaceSessionKey
      ) === '1'
    );
  }

  function markSurfaceSeen() {
    writeSession(
      CONFIG.surfaceSessionKey,
      '1'
    );
  }

  function exitShown() {
    return (
      sessionValue(
        CONFIG.exitShownSessionKey
      ) === '1'
    );
  }

  function markExitShown() {
    writeSession(
      CONFIG.exitShownSessionKey,
      '1'
    );

    markSurfaceSeen();

    /*
     * Bottom Sheetの自動表示も
     * このセッションでは止める
     */
    writeSession(
      'sct_rec_bottom_auto_v1',
      '1'
    );
  }

  function sessionHasAtc() {
    const value =
      sessionValue(
        CONFIG.sessionAtcKey
      );

    return (
      value === 'true' ||
      value === '1'
    );
  }

  function dismissedRecently() {
    const dismissedAt =
      localNumber(
        CONFIG.dismissedAtKey
      );

    if (!dismissedAt) {
      return false;
    }

    return (
      Date.now() - dismissedAt <
      CONFIG.dismissDays *
        24 *
        60 *
        60 *
        1000
    );
  }

  function markDismissed() {
    writeLocalNumber(
      CONFIG.dismissedAtKey,
      Date.now()
    );
  }

  function getPanel() {
    return document.getElementById(
      'SctExitRecommendation'
    );
  }

  function getTrack() {
    return document.querySelector(
      '[data-sct-exit-track]'
    );
  }

  function modalOrDrawerOpen() {
    return (
      document.body.classList.contains(
        'overflow-hidden'
      ) ||
      Boolean(
        document.querySelector(
          'cart-drawer.active'
        )
      )
    );
  }

  function canTrigger() {
    const minimumViews =
      coarsePointer
        ? CONFIG.mobileMinViewedProducts
        : CONFIG.minViewedProducts;

    return (
      viewedCount() >= minimumViews &&
      Date.now() - pageLoadedAt >=
        CONFIG.minPageAgeMs &&
      !exitShown() &&
      !surfaceSeen() &&
      !sessionHasAtc() &&
      !dismissedRecently() &&
      !modalOrDrawerOpen() &&
      document.visibilityState ===
        'visible'
    );
  }

  function formatPrice(priceCents) {
    const cents =
      toNumber(priceCents);

    if (cents <= 0) return '';

    const context =
      readContext();

    const currency =
      context.currency ||
      'JPY';

    const language =
      context.language ||
      document.documentElement.lang ||
      'en';

    const noDecimals =
      currency === 'JPY' ||
      currency === 'KRW';

    try {
      return new Intl.NumberFormat(
        language,
        {
          style: 'currency',
          currency,
          minimumFractionDigits:
            noDecimals ? 0 : 2,
          maximumFractionDigits:
            noDecimals ? 0 : 2,
        }
      ).format(
        cents / 100
      );
    } catch (_error) {
      return String(
        Math.round(cents / 100)
      );
    }
  }

  function textElement(
    tagName,
    className,
    text
  ) {
    const node =
      document.createElement(
        tagName
      );

    node.className =
      className;

    node.textContent =
      text || '';

    return node;
  }

  function createCard(
    product,
    position
  ) {
    const article =
      document.createElement(
        'article'
      );

    article.className =
      'sct-exit-rec-card';

    article.setAttribute(
      'role',
      'listitem'
    );

    const link =
      document.createElement(
        'a'
      );

    link.className =
      'sct-exit-rec-card__link';

    link.href =
      product.url ||
      `/products/${product.handle}`;

    link.dataset.sctAction =
      'exit_recommendation_product_click';

    link.dataset.sctItemType =
      'product';

    link.dataset.sctItemId =
      String(product.id || '');

    link.dataset.sctItemHandle =
      product.handle || '';

    link.dataset.sctItemName =
      product.title || '';

    link.dataset.sctItemPosition =
      String(position);

    link.dataset.sctProductPosition =
      String(position);

    link.dataset.sctBrand =
      product.vendor || '';

    link.dataset.sctTabName =
      'exit_recommendation';

    link.dataset.sctDestinationType =
      'product';

    link.addEventListener(
      'click',
      () => {
        window.SctRecommendation
          ?.track?.(
            'click',
            {
              id: product.id,
              handle:
                product.handle,
              title:
                product.title,
              vendor:
                product.vendor,
              category:
                product.category,
              price_cents:
                product.price_cents,
              available:
                product.available,
              created_at:
                product.created_at,
              styles:
                product.styles,
            },
              {
                recommendation_source:
                  'exit_recommendation',
              }
            );
      },
      { passive: true }
    );

    const media =
      document.createElement(
        'div'
      );

    media.className =
      'sct-exit-rec-card__media';

    if (product.image) {
      const image =
        document.createElement(
          'img'
        );

      image.className =
        'sct-exit-rec-card__image';

      image.src =
        product.image;

      image.alt =
        product.title || '';

      image.loading =
        'lazy';

      image.decoding =
        'async';

      media.appendChild(image);
    } else {
      const placeholder =
        document.createElement(
          'div'
        );

      placeholder.className =
        'sct-exit-rec-card__placeholder';

      media.appendChild(
        placeholder
      );
    }

    const info =
      document.createElement(
        'div'
      );

    info.className =
      'sct-exit-rec-card__info';

    if (product.vendor) {
      info.appendChild(
        textElement(
          'p',
          'sct-exit-rec-card__vendor',
          product.vendor
        )
      );
    }

    info.appendChild(
      textElement(
        'p',
        'sct-exit-rec-card__title',
        product.title
      )
    );

    const price =
      formatPrice(
        product.price_cents
      );

    if (price) {
      info.appendChild(
        textElement(
          'p',
          'sct-exit-rec-card__price',
          price
        )
      );
    }

    link.appendChild(media);
    link.appendChild(info);
    article.appendChild(link);

    return article;
  }

  function renderProducts() {
    const track = getTrack();

    if (!track) {
      return false;
    }

    track.textContent = '';

    products
      .slice(
        0,
        CONFIG.maxDisplayProducts
      )
      .forEach(
        (product, index) => {
          track.appendChild(
            createCard(
              product,
              index + 1
            )
          );
        }
      );

    return true;
  }

  async function getProducts() {
    if (
      products.length >=
      CONFIG.minDisplayProducts
    ) {
      return products;
    }

    if (loadingPromise) {
      return loadingPromise;
    }

    const engine =
      window
        .SctRecommendationEngine;

    if (!engine?.recommend) {
      return [];
    }

    const last =
      engine.getLastResult?.();

    if (
      Array.isArray(last?.products) &&
      last.products.length >=
        CONFIG.minDisplayProducts
    ) {
      products =
        last.products.slice(
          0,
          CONFIG.maxDisplayProducts
        );

      return products;
    }

    loadingPromise =
      engine.recommend()
        .then(result => {
          const resultProducts =
            Array.isArray(
              result?.products
            )
              ? result.products
              : [];

          products =
            resultProducts.slice(
              0,
              CONFIG.maxDisplayProducts
            );

          return products;
        })
        .catch(error => {
          if (debug) {
            console.warn(
              '[SCT Exit Recommendation] Candidate load failed.',
              error
            );
          }

          return [];
        })
        .finally(() => {
          loadingPromise = null;
        });

    return loadingPromise;
  }

  function openPanel(source) {
    const panel = getPanel();

    if (!panel) return false;

    markExitShown();

    panel.hidden = false;

    panel.setAttribute(
      'aria-hidden',
      'false'
    );

    requestAnimationFrame(
      () => {
        panel.classList.add(
          'is-open'
        );
      }
    );

    window.SctAnalytics
      ?.track?.(
        panel,
        {
          action_type:
            'exit_recommendation_impression',
          item_type:
            'recommendation',
          tab_name:
            source,
        }
      );

    if (debug) {
      console.log(
        '[SCT Exit Recommendation] Opened.',
        source
      );
    }

    return true;
  }

  function closePanel() {
    const panel = getPanel();

    if (!panel) return;

    panel.classList.remove(
      'is-open'
    );

    panel.setAttribute(
      'aria-hidden',
      'true'
    );

    markDismissed();

    window.setTimeout(
      () => {
        panel.hidden = true;
      },
      300
    );
  }

  async function tryOpen(source) {
    if (
      opening ||
      !canTrigger()
    ) {
      return;
    }

    opening = true;

    try {
      const resultProducts =
        await getProducts();

      if (
        !canTrigger() ||
        resultProducts.length <
          CONFIG.minDisplayProducts
      ) {
        return;
      }

      if (!renderProducts()) {
        return;
      }

      openPanel(source);
    } finally {
      opening = false;
    }
  }

  function bindClose() {
    document.querySelector(
      '[data-sct-exit-close]'
    )?.addEventListener(
      'click',
      closePanel
    );

    document.addEventListener(
      'keydown',
      event => {
        if (
          event.key !== 'Escape'
        ) {
          return;
        }

        const panel = getPanel();

        if (
          !panel ||
          panel.hidden ||
          !panel.classList.contains(
            'is-open'
          )
        ) {
          return;
        }

        closePanel();
      }
    );
  }

  function bindDesktopExit() {
    if (coarsePointer) return;

    document.addEventListener(
      'mouseout',
      event => {
        if (
          event.relatedTarget !== null ||
          event.clientY >
            CONFIG.desktopExitTopPx
        ) {
          return;
        }

        tryOpen(
          'desktop_top_exit'
        );
      }
    );
  }

  function bindMobileExitSignal() {
    if (!coarsePointer) return;

    window.addEventListener(
      'scroll',
      () => {
        const y =
          window.scrollY ||
          document.documentElement.scrollTop ||
          0;

        const now =
          Date.now();

        if (
          y >=
          CONFIG.mobileDeepScrollPx
        ) {
          lastDeepSeenAt = now;
          return;
        }

        if (
          y <= CONFIG.mobileTopPx &&
          lastDeepSeenAt &&
          now - lastDeepSeenAt <=
            CONFIG.mobileReturnWindowMs
        ) {
          lastDeepSeenAt = 0;

          tryOpen(
            'mobile_fast_return_top'
          );
        }
      },
      { passive: true }
    );
  }

  function resetDebugState() {
    if (!debug) {
      return false;
    }

    try {
      sessionStorage.removeItem(
        CONFIG.exitShownSessionKey
      );

      sessionStorage.removeItem(
        CONFIG.surfaceSessionKey
      );

      sessionStorage.removeItem(
        CONFIG.sessionAtcKey
      );

      sessionStorage.removeItem(
        'sct_rec_bottom_auto_v1'
      );

      localStorage.removeItem(
        CONFIG.dismissedAtKey
      );

      const panel = getPanel();

      if (panel) {
        panel.classList.remove(
          'is-open'
        );

        panel.hidden = true;

        panel.setAttribute(
          'aria-hidden',
          'true'
        );
      }

      return true;
    } catch (_error) {
      return false;
    }
  }

  async function forceOpenForDebug() {
    if (!debug) {
      return {
        opened: false,
        reason: 'debug_mode_required',
      };
    }

    const resultProducts =
      await getProducts();

    if (
      resultProducts.length <
      CONFIG.minDisplayProducts
    ) {
      return {
        opened: false,
        reason: 'not_enough_products',
        products: resultProducts.length,
      };
    }

    if (!renderProducts()) {
      return {
        opened: false,
        reason: 'render_failed',
      };
    }

    const bottomSheet =
      document.getElementById(
        'SctRecommendationBottomSheet'
      );

    if (bottomSheet) {
      bottomSheet.classList.remove(
        'is-open'
      );

      bottomSheet.hidden = true;

      bottomSheet.setAttribute(
        'aria-hidden',
        'true'
      );
    }

    const trigger =
      document.getElementById(
        'SctRecommendationBottomTrigger'
      );

    if (trigger) {
      trigger.hidden = true;
    }

    openPanel(
      'debug_force'
    );

    return {
      opened: true,
      products: resultProducts.length,
    };
  }

  function debugStatus() {
    return {
      debug,
      coarsePointer,
      viewed: viewedCount(),
      pageAgeMs:
        Date.now() - pageLoadedAt,
      exitShown: exitShown(),
      surfaceSeen: surfaceSeen(),
      sessionAtc: sessionHasAtc(),
      dismissed: dismissedRecently(),
      canTrigger: canTrigger(),
      productsLoaded: products.length,
    };
  }

  function isTreatmentGroup() {
    return (
      window
        .SctRecommendationExperiment
        ?.group ===
      'treatment'
    );
  }

  function init() {
    if (!isTreatmentGroup()) {
      if (debug) {
        console.log(
          '[SCT Exit Recommendation] Control group: UI suppressed.'
        );
      }

      return;
    }

    bindClose();
    bindDesktopExit();
    bindMobileExitSignal();

    if (debug) {
      console.log(
        '[SCT Exit Recommendation] Ready.',
        {
          coarsePointer,
          viewed:
            viewedCount(),
          surfaceSeen:
            surfaceSeen(),
          sessionAtc:
            sessionHasAtc(),
          dismissed:
            dismissedRecently(),
        }
      );
    }
  }

  if (debug) {
    window.SctExitRecommendationDebug =
      Object.freeze({
        reset() {
          return resetDebugState();
        },

        forceOpen() {
          return forceOpenForDebug();
        },

        status() {
          return debugStatus();
        },
      });

    if (
      query.get(
        'sct_exit_test'
      ) === '1'
    ) {
      window.setTimeout(
        () => {
          forceOpenForDebug();
        },
        800
      );
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();